import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import dayjs from "dayjs";
import { useSnackbar } from "notistack";
import { useIntl } from "react-intl";
import UserContext from "context/UserContext";
import RevaText from "components/common/RevaText";
import performRequest from "network/perform-request";
import { organizationMembershipPaymentsRoute } from "network/api-routes";
import { membershipCurrencySymbol } from "utils/membership-ui-helpers";

const formatMoney = (amount, currencyCode) => {
  const symbol = membershipCurrencySymbol(currencyCode);
  const value = amount == null || amount === "" ? "—" : Number(amount).toLocaleString();
  return symbol ? `${symbol} ${value}` : String(value);
};

const formatDate = (value) => (value ? dayjs(value).format("DD/MM/YYYY") : "—");

/**
 * Paginated membership payments for the org dashboard.
 * Pass `refreshKey` (number) and bump it to reload after import (Task 6).
 */
const OrganizationMembershipPaymentsList = ({ refreshKey = 0, currencyCode = null }) => {
  const intl = useIntl();
  const { user, selectedOrganization } = useContext(UserContext);
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ page: 1, page_size: 25, total: 0 });

  const loadPayments = useCallback(
    (nextPage = 1) => {
      if (!selectedOrganization?.id) return;
      setLoading(true);
      performRequest(
        "get",
        organizationMembershipPaymentsRoute(selectedOrganization.id),
        { page: nextPage },
        true,
        user,
      )
        .then((res) => {
          const payload = res.data || {};
          setRows(Array.isArray(payload.data) ? payload.data : []);
          setMeta(payload.meta || { page: nextPage, page_size: 25, total: 0 });
          setPage(payload.meta?.page || nextPage);
        })
        .catch((error) =>
          enqueueSnackbar(
            error?.response?.data?.message || intl.formatMessage({ id: "org.membership.payments.loadError" }),
            { variant: "error" },
          ),
        )
        .finally(() => setLoading(false));
    },
    [enqueueSnackbar, intl, selectedOrganization, user],
  );

  useEffect(() => {
    setPage(1);
    loadPayments(1);
  }, [loadPayments, refreshKey]);

  if (!selectedOrganization) return null;

  const pageSize = meta.page_size || 25;
  const total = meta.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const hasPrev = page > 1;
  const hasNext = page < totalPages;

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
        <RevaText variant="h6">{intl.formatMessage({ id: "org.membership.sections.payments" })}</RevaText>
        {loading ? <CircularProgress size={18} /> : null}
      </Stack>

      {loading && rows.length === 0 ? (
        <CircularProgress size={28} />
      ) : rows.length === 0 ? (
        <RevaText color="text.secondary">{intl.formatMessage({ id: "org.membership.payments.empty" })}</RevaText>
      ) : (
        <>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{intl.formatMessage({ id: "org.membership.payments.date" })}</TableCell>
                <TableCell>{intl.formatMessage({ id: "org.membership.payments.member" })}</TableCell>
                <TableCell align="right">{intl.formatMessage({ id: "org.membership.payments.amount" })}</TableCell>
                <TableCell>{intl.formatMessage({ id: "org.membership.payments.method" })}</TableCell>
                <TableCell>{intl.formatMessage({ id: "org.membership.payments.description" })}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{formatDate(row.paid_at)}</TableCell>
                  <TableCell>{row.member_name || "—"}</TableCell>
                  <TableCell align="right">{formatMoney(row.amount, currencyCode)}</TableCell>
                  <TableCell>{row.method || "—"}</TableCell>
                  <TableCell>{row.description || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 2 }}>
            <Button size="small" variant="outlined" disabled={!hasPrev || loading} onClick={() => loadPayments(page - 1)}>
              {intl.formatMessage({ id: "org.membership.payments.prev" })}
            </Button>
            <RevaText variant="body2">
              {intl.formatMessage(
                { id: "org.membership.payments.pageInfo" },
                { page, totalPages, total },
              )}
            </RevaText>
            <Button size="small" variant="outlined" disabled={!hasNext || loading} onClick={() => loadPayments(page + 1)}>
              {intl.formatMessage({ id: "org.membership.payments.next" })}
            </Button>
          </Stack>
        </>
      )}
    </Box>
  );
};

export default OrganizationMembershipPaymentsList;
